import * as StompJs from '@stomp/stompjs';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import moment from 'moment';
import { AttachmentsToBeUploadedViewModel, ITelephonyConfiguration, ObservablePageCollectionController } from '.';
import { RemoteResourceEventsViewModel } from './RemoteResourceEventsViewModel';
import * as Api from './sdk';
import * as VmUtils from './Utils';
import * as ViewModels from './ViewModels';
import { UserSessionContext, ViewModel } from './viewModels/index';

export interface IParentVMCallbacks {
	getAllUnreadMessages?: () => void;
	sortConversations?: () => void;
}

export enum Direction {
	Inbound = 'Inbound',
	Outbound = 'Outbound',
}

export enum TextStatus {
	Unknown = 'Unknown',
	Sending = 'Sending',
	Sent = 'Sent',
	Delivered = 'Delivered',
	Failed = 'Failed',
	Received = 'Received',
}

export interface ITextContact extends Api.IContact {
	selectedPhoneNumber: Api.IPhoneNumber;
}

export interface ITextRecipient {
	contact: Api.IPrincipal;
	number: Api.IPhoneNumberMetadata;
}

export interface IConversation extends Api.IBaseResourceModel {
	automatedSMSOptOutDate: string;
	earliestUnreadMessageDate: string;
	isSpam: boolean | null;
	levitateNumber: Api.IPhoneNumberMetadata;
	inboundMessageCount: number;
	lastMessage: ITextMessage;
	outboundMessageCount: number;
	phoneNumberId: string;
	toNumbers: ITextRecipient[];
	unreadMessageCount: number;
}

export interface ITextMessage extends Api.IBaseResourceModel {
	bandwidthId: string;
	canRetry: boolean;
	conversationId: string;
	direction: Direction;
	errorMessage: string;
	media: Api.IFileAttachment[];
	owner: Api.IPhoneNumberMetadata;
	readDate: string;
	segmentCount: number;
	sentDate?: string;
	status: TextStatus;
	text: string;
}

export interface ITextMessagingRemoteResourceEvent {
	conversationId: string;
	id: string;
}

export type ITextMessageRemoteResourceEvent = ITextMessagingRemoteResourceEvent;

export interface ITextMessageCreatedRemoteResourceEvent extends ITextMessageRemoteResourceEvent {
	direction?: Direction;
}

export interface ITextMessageUpdatedRemoteResourceEvent extends ITextMessageRemoteResourceEvent {
	status: TextStatus;
}

class TextMessagingEventsViewModel<
	TUserSession extends UserSessionContext = UserSessionContext,
> extends RemoteResourceEventsViewModel<TUserSession, any> {
	private mOnTextMessageCreated: (events: Api.IRemoteEvent<ITextMessageCreatedRemoteResourceEvent>[]) => void;
	private mOnTextMessageUpdated: (events: Api.IRemoteEvent<ITextMessageUpdatedRemoteResourceEvent>[]) => void;
	private disconnectCb?: () => void = null;

	constructor(userSession: TUserSession, resource: any, eventLogger?: Api.IEventLoggingService) {
		super(userSession, resource, '#', eventLogger);

		makeObservable<TextMessagingEventsViewModel, 'onMessage'>(this, {
			onMessage: action,
		});

		this.mUserSession = userSession;
	}

	protected composeRoute() {
		return `User.${this.mUserSession?.user?.id}.TextMessage.#`;
	}

	public set onTextMessageCreated(callback: (
		events: Api.IRemoteEvent<ITextMessageCreatedRemoteResourceEvent>[]
	) => void) {
		this.mOnTextMessageCreated = callback;
	}

	public set onTextMessageUpdated(callback: (
		events: Api.IRemoteEvent<ITextMessageUpdatedRemoteResourceEvent>[]
	) => void) {
		this.mOnTextMessageUpdated = callback;
	}

	public connect = async (stompConfig?: StompJs.StompConfig, disconnectCb?: () => void) => {
		this.disconnectCb = disconnectCb;
		const remoteEvents = await this.mRemoteEvents.connect(stompConfig);
		this.onConnect();
		return remoteEvents;
	};

	public permananentlyDisconnect() {
		const promise = super.permananentlyDisconnect();
		this.mOnTextMessageCreated = null;
		this.mOnTextMessageUpdated = null;
		this.disconnectCb?.();
		console.log('%cTexting disconnected:', 'color: red;', new Date());
		return promise;
	}

	protected onMessage(events: Api.IRemoteEvent<any>[]) {
		if (this.mOnMessageCallback) {
			this.mOnMessageCallback(events);
		}

		if (this.mOnTextMessageCreated) {
			const createdEvents = events.filter(
				x => x.valueType === 'TextMessageCreate'
			) as Api.IRemoteEvent<ITextMessageCreatedRemoteResourceEvent>[];
			if (createdEvents.length > 0) {
				this.mOnTextMessageCreated(createdEvents);
			}
		}

		if (this.mOnTextMessageUpdated) {
			const updatedEvents = events.filter(
				x => x.valueType === 'TextMessageUpdate'
			) as Api.IRemoteEvent<ITextMessageUpdatedRemoteResourceEvent>[];
			if (updatedEvents.length > 0) {
				this.mOnTextMessageUpdated(updatedEvents);
			}
		}
	}
}
export type IConversationRemoteResourceEvent = ITextMessagingRemoteResourceEvent;

export type ITypingIndicator = Api.IDictionary<string>;

export interface IConversationUpdatedRemoteResourceEvent extends IConversationRemoteResourceEvent {
	status: TextStatus;
	typingIndicator: ITypingIndicator;
}

class ConversationEventsViewModel<
	TUserSession extends UserSessionContext = UserSessionContext,
> extends RemoteResourceEventsViewModel<TUserSession, any> {
	private mOnConversationUpdated: (events: Api.IRemoteEvent<IConversationUpdatedRemoteResourceEvent>[]) => void;
	protected disconnectCb?: () => void = null;

	constructor(userSession: TUserSession, resource: any, eventLogger?: Api.IEventLoggingService) {
		super(userSession, resource, '#', eventLogger);

		makeObservable<ConversationEventsViewModel, 'onMessage'>(this, {
			onMessage: action,
		});
	}

	protected composeRoute() {
		return `User.${this.mUserSession.user.id}.Conversation.#`;
	}

	public set onConversationUpdated(callback: (
		events: Api.IRemoteEvent<IConversationUpdatedRemoteResourceEvent>[]
	) => void) {
		this.mOnConversationUpdated = callback;
	}

	public connect = (stompConfig?: StompJs.StompConfig, disconnectCb?: () => void) => {
		this.disconnectCb = disconnectCb;
		return this.mRemoteEvents.connect(stompConfig)?.then(this.onConnect);
	};

	public permananentlyDisconnect() {
		const promise = super.permananentlyDisconnect();
		this.mOnConversationUpdated = null;
		this.disconnectCb?.();
		return promise;
	}

	protected onMessage(events: Api.IRemoteEvent<any>[]) {
		if (this.mOnMessageCallback) {
			this.mOnMessageCallback(events);
		}

		if (this.mOnConversationUpdated) {
			const updatedEvents = events.filter(
				x => x.valueType === 'ConversationUpdate'
			) as Api.IRemoteEvent<IConversationUpdatedRemoteResourceEvent>[];
			if (updatedEvents.length > 0) {
				this.mOnConversationUpdated(updatedEvents);
			}
		}
	}
}

export class TypingIndicatorEventsViewModel extends ConversationEventsViewModel {
	private mUsersTyping: Api.IUserReference[];
	private mLastUpdateEvent: Api.IRemoteEvent<IConversationUpdatedRemoteResourceEvent>;
	private phoneNumber: ITelephonyConfiguration;
	private mRefreshTimerHandle: any;

	constructor(userSession: UserSessionContext, phone: ITelephonyConfiguration, eventLogger?: Api.IEventLoggingService) {
		super(userSession, '#', eventLogger);

		makeObservable<TypingIndicatorEventsViewModel, 'mUsersTyping'>(this, {
			mUsersTyping: observable.ref,
			usersTyping: computed,
		});

		this.phoneNumber = phone;
		this.mUsersTyping = [];
		this.onConversationUpdated = this.onConversationUpdatedHandler;
	}

	public connect = async (stompConfig?: StompJs.StompConfig, disconnectCb?: () => void) => {
		this.disconnectCb = disconnectCb;
		await this.mRemoteEvents.connect(stompConfig).then(this.onConnect);
	};

	public get usersTyping() {
		return this.mUsersTyping;
	}

	public getAssignedUsers() {
		return this.phoneNumber.assignedUserIds;
	}

	public onConversationUpdatedHandler = (events: Api.IRemoteEvent<IConversationUpdatedRemoteResourceEvent>[]) => {
		for (const event of events) {
			this.mLastUpdateEvent = event;
			this.checkForTypingChanges(event);
		}
	};

	public permananentlyDisconnect() {
		const promise = super.permananentlyDisconnect();
		this.clearTimer();
		return promise;
	}

	private checkForTypingChanges = (event = this.mLastUpdateEvent) => {
		this.mLastUpdateEvent = event;
		const oldMoment = moment().subtract(1, 'minute');
		const eventIndicators = event.value.typingIndicator || {};
		const idsForUsersCurrentlyTyping = Object.keys(eventIndicators).reduce((result, userId) => {
			const indicatorMoment = moment(eventIndicators[userId]);
			// check time and exclude current user
			if (indicatorMoment.isAfter(oldMoment) && userId !== this.mUserSession.user.id) {
				result.push(userId);
			}
			return result;
		}, [] as string[]);

		// update collection
		this.mUsersTyping = idsForUsersCurrentlyTyping
			.map(userId => this.phoneNumber.assignedUsers.find(x => x.id === userId))
			.filter(x => !!x);

		this.mRefreshTimerHandle = setTimeout(() => {
			this.checkForTypingChanges();
		}, 20000); // checks every 20 sec
	};

	private clearTimer = () => {
		if (this.mRefreshTimerHandle) {
			clearTimeout(this.mRefreshTimerHandle);
			this.mRefreshTimerHandle = null;
		}
	};
}

export class ConversationViewModel extends ViewModel {
	private mConversation: IConversation;
	private mTextMessagesPageCollectionController: ObservablePageCollectionController<ITextMessage, ITextMessage>;
	private sending = false;
	private fetching = false;
	private mParentVMCallbacks: IParentVMCallbacks;

	public static defaultSort = (a: ITextMessage, b: ITextMessage) => {
		const dateA = new Date(a.creationDate);
		const dateB = new Date(b.creationDate);
		if (dateA < dateB) {
			return 1;
		} else if (dateB < dateA) {
			return -1;
		} else {
			return 0;
		}
	};

	constructor(userSession: UserSessionContext, conversation?: IConversation, parentVmCallbacks?: IParentVMCallbacks) {
		super(userSession);

		makeObservable<
			ConversationViewModel,
			| 'mConversation'
			| 'mTextMessagesPageCollectionController'
			| 'sending'
			| 'fetching'
			| 'mParentVMCallbacks'
			| 'sortMessages'
		>(this, {
			mConversation: observable,
			mTextMessagesPageCollectionController: observable,
			sending: observable,
			fetching: observable,
			mParentVMCallbacks: observable,
			isLoading: override,
			id: computed,
			levitateNumber: computed,
			inboundMessageCount: computed,
			lastMessage: computed,
			earliestUnreadMessageDate: computed,
			outboundMessageCount: computed,
			phoneNumberId: computed,
			toNumbers: computed,
			unreadMessageCount: computed,
			textMessages: computed,
			isSending: computed,
			isFetching: computed,
			allMessagesLoaded: computed,
			automatedSMSOptOutDate: computed,
			isSpam: computed,
			load: override,
			getMessage: action,
			getMessages: action,
			markAsRead: action,
			markAsUnread: action,
			sendMessage: action,
			updateRecipients: action,
			sortMessages: action,
		});

		this.mConversation = conversation;
		this.mParentVMCallbacks = parentVmCallbacks;
		this.mTextMessagesPageCollectionController = new ObservablePageCollectionController<ITextMessage>({
			apiPath: `conversation/${this.id}/messages`,
			client: this.userSession.webServiceHelper,
			httpMethod: 'GET',
		});
	}

	get isLoading() {
		return this.mTextMessagesPageCollectionController.isFetching;
	}

	get id() {
		return this.mConversation?.id || '';
	}

	get levitateNumber() {
		return this.mConversation.levitateNumber;
	}

	get inboundMessageCount() {
		return this.mConversation.inboundMessageCount;
	}

	get lastMessage() {
		return this.mConversation.lastMessage;
	}

	get earliestUnreadMessageDate() {
		return this.mConversation.earliestUnreadMessageDate;
	}

	get outboundMessageCount() {
		return this.mConversation.outboundMessageCount;
	}

	get phoneNumberId() {
		return this.mConversation.phoneNumberId;
	}

	get toNumbers() {
		return this.mConversation.toNumbers;
	}

	get unreadMessageCount() {
		return this.mConversation.unreadMessageCount;
	}

	get textMessages() {
		return this.mTextMessagesPageCollectionController.fetchResults;
	}

	get isSending() {
		return this.sending;
	}

	get isFetching() {
		return this.fetching;
	}

	get allMessagesLoaded() {
		return this.mTextMessagesPageCollectionController.hasFetchedAllPages;
	}

	public get automatedSMSOptOutDate() {
		return this.mConversation?.automatedSMSOptOutDate ? new Date(this.mConversation?.automatedSMSOptOutDate) : null;
	}

	public get isSpam() {
		return !!this.mConversation?.isSpam;
	}

	public setConversation = (conversation: IConversation) => {
		this.mConversation = conversation;
	};

	public load = async () => {
		const conversation = await this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `conversation/${this.id}/` }),
			'GET'
		);
		if (conversation.success) {
			this.mConversation = conversation.value;
			this.loaded = true;
		}
	};

	public getMessage = async (id: string) => {
		this.busy = true;

		return Promise.all([
			this.userSession.webServiceHelper.callWebServiceAsync<ITextMessage>(
				this.composeApiUrl({ urlPath: `conversation/${this.id}/message/${id}` }),
				'GET'
			),
			this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
				this.composeApiUrl({ urlPath: `conversation/${this.id}` }),
				'GET'
			),
		]).then(([message, convo]) => {
			const errors: Api.IOperationResultNoValue[] = [];
			if (message.success) {
				const tm = this.mTextMessagesPageCollectionController.fetchResults.find(t => t.id === id);
				const index = this.mTextMessagesPageCollectionController.fetchResults.indexOf(tm);
				if (index > -1) {
					this.mTextMessagesPageCollectionController.fetchResults.setItemAtIndex(message.value, index);
				} else {
					this.mTextMessagesPageCollectionController.fetchResults.add(message.value);
				}

				this.sortMessages();
			} else {
				errors.push(Api.asApiError(message));
			}

			if (convo.success) {
				this.mConversation = convo.value;
			} else {
				errors.push(Api.asApiError(convo));
			}

			this.busy = false;

			if (errors.length) {
				throw Api.asApiError(errors[0]);
			}
		});
	};

	public getMessages = async () => {
		this.fetching = true;
		await this.mTextMessagesPageCollectionController.getNext();
		this.fetching = false;
		this.loaded = true;
	};

	public markAsRead = async () => {
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `Conversation/${this.id}/read` }),
			'PUT'
		);

		if (result.success) {
			this.mConversation = result.value;
		} else {
			throw Api.asApiError(result);
		}

		this.mParentVMCallbacks?.getAllUnreadMessages?.();
	};

	public markAsUnread = async () => {
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `Conversation/${this.id}/unread` }),
			'PUT'
		);

		if (result.success) {
			this.mConversation = result.value;
		} else {
			throw Api.asApiError(result);
		}

		this.mParentVMCallbacks?.getAllUnreadMessages?.();
	};

	public sendMessage = async (msg: string, attachments: AttachmentsToBeUploadedViewModel<File>) => {
		this.sending = true;

		const data = new FormData();

		const value: { text?: string } = {};

		if (msg) {
			value.text = msg;
		}

		data.append('value', JSON.stringify(value));

		if (attachments.attachments.length) {
			attachments.attachments.forEach(attachment => {
				data.append('files', attachment);
			});
		}

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<ITextMessage>(
			this.composeApiUrl({ urlPath: `conversation/${this.id}/send` }),
			'POST',
			data
		);

		if (result.success) {
			this.mTextMessagesPageCollectionController.fetchResults.add(result.value);
			this.sortMessages();
			this.mParentVMCallbacks?.sortConversations?.();
			this.sending = false;
		} else {
			this.sending = false;
			throw Api.asApiError(result);
		}
	};

	public updateRecipients = async (entity: ITextContact) => {
		this.busy = true;
		const regex = /\d+/g;
		this.mConversation.toNumbers = this.toNumbers.map(num => {
			let phoneNumberMatch: Api.IPhoneNumber;
			if (entity.selectedPhoneNumber) {
				const phoneNumber = entity.selectedPhoneNumber.metadata?.standard || entity.selectedPhoneNumber?.value;
				const parsed = phoneNumber.match(regex).join('');
				if (num.number.e164.includes(parsed)) {
					phoneNumberMatch = entity.selectedPhoneNumber;
				}
			} else {
				phoneNumberMatch = entity.phoneNumbers?.find(p => {
					const parsed = p.value.match(regex).join('');
					return num.number.e164.includes(parsed);
				});
			}

			if (phoneNumberMatch) {
				num.contact = entity;
			}

			return num;
		});

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `conversation/${this.id}/recipients` }),
			'PUT',
			this.toNumbers
		);

		if (result.success) {
			this.mConversation = result.value;
			this.busy = false;
		} else {
			this.busy = false;
			throw Api.asApiError(result);
		}
	};

	private sortMessages = () => {
		this.mTextMessagesPageCollectionController.fetchResults.sort(ConversationViewModel.defaultSort);
	};
}
export const ONE_KB = 1024;
export const MAX_FILE_SIZE_MB = 3584;
export const TextMessagingMaxFileByteSize = MAX_FILE_SIZE_MB * ONE_KB; // 3.5MB

export function convertToMB(fileSizeInKB: number): string {
	const fileSizeInMB = fileSizeInKB / ONE_KB;
	return `${fileSizeInMB.toFixed(1)}MB`;
}

export class TextMessagingViewModel extends ViewModel {
	protected mEventsViewModel: TextMessagingEventsViewModel;
	protected mPhoneNumberOrders: Api.ITelephonyConfiguration[];
	protected mConversationsPageCollectionController: ObservablePageCollectionController<
		IConversation,
		ConversationViewModel
	>;
	protected mLoadingConversations = false;
	protected mConversationsLoaded = false;
	protected mTotalUnreadMessages: number;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable<
			TextMessagingViewModel,
			| 'mEventsViewModel'
			| 'mPhoneNumberOrders'
			| 'mConversationsPageCollectionController'
			| 'mLoadingConversations'
			| 'mConversationsLoaded'
			| 'mTotalUnreadMessages'
			| 'onTextMessageCreated'
			| 'onTextMessageUpdated'
			| 'sortConversations'
		>(this, {
			mEventsViewModel: observable.ref,
			mPhoneNumberOrders: observable,
			mConversationsPageCollectionController: observable,
			mLoadingConversations: observable,
			mConversationsLoaded: observable,
			mTotalUnreadMessages: observable,
			phoneNumberOrder: computed,
			callForwardingMetadata: computed,
			conversationApiUrl: computed,
			connectionState: computed,
			conversations: computed,
			creator: computed,
			currentUserOwnsNumber: computed,
			id: computed,
			loadingConversations: computed,
			number: computed,
			orderId: computed,
			supportsGroupTexting: computed,
			allConversationsLoaded: computed,
			conversationsLoaded: computed,
			totalUnreadMessages: computed,
			conversationCallbacks: computed,
			onArchiveConversation: action,
			markAsSpam: action,
			onUnmarkConversationAsSpam: action,
			toggleUnsubscribeConversation: action,
			updateTypingIndicator: action,
			createConversation: action,
			getConversation: action,
			addConversations: action,
			getConversationByPhoneNumbers: action,
			getConversations: action,
			fullReset: action,
			getAllUnreadMessages: action,
			getUserPhone: action,
			onTextMessageCreated: action,
			onTextMessageUpdated: action,
			init: action,
			reset: action,
			sortConversations: action,
		});
	}

	get phoneNumberOrder() {
		return this.mPhoneNumberOrders?.filter(phoneNumberOrder => {
			return (
				phoneNumberOrder.connectionState !== Api.ConnectionState.Disconnected &&
				phoneNumberOrder.connectionState !== Api.ConnectionState.Failed
			);
		})?.[0];
	}

	get callForwardingMetadata() {
		return this.phoneNumberOrder?.callForwardingNumber?.phoneNumber;
	}

	get conversationApiUrl() {
		return `conversation/phoneNumber/${this.id}`;
	}

	get connectionState() {
		return this.phoneNumberOrder?.connectionState;
	}

	get conversations() {
		return this.mConversationsPageCollectionController?.fetchResults;
	}

	get creator() {
		return this.phoneNumberOrder?.creator;
	}

	get currentUserOwnsNumber() {
		return this.phoneNumberOrder?.creator?.id === this.userSession?.user?.id;
	}

	get id() {
		return this.phoneNumberOrder?.id;
	}

	get loadingConversations() {
		return this.mLoadingConversations;
	}

	get number() {
		return this.phoneNumberOrder?.number;
	}

	get orderId() {
		return this.phoneNumberOrder?.orderId;
	}

	get supportsGroupTexting() {
		return this.phoneNumberOrder?.supportsGroupTexting;
	}

	get allConversationsLoaded() {
		return this.mConversationsPageCollectionController?.hasFetchedAllPages;
	}

	get conversationsLoaded() {
		return this.mConversationsLoaded;
	}

	get totalUnreadMessages() {
		return this.mTotalUnreadMessages || 0;
	}

	get conversationCallbacks() {
		return {
			getAllUnreadMessages: this.getAllUnreadMessages,
			sortConversations: this.sortConversations,
		};
	}

	protected mTransformConversation = (conversation: IConversation) => {
		return new ConversationViewModel(this.userSession, conversation, this.conversationCallbacks);
	};

	public get textMessageEvents() {
		return this.mEventsViewModel;
	}

	public onArchiveConversation = (id: string) => {
		const conversation = this.mConversationsPageCollectionController?.fetchResults.getById(id);
		if (conversation) {
			this.mConversationsPageCollectionController?.fetchResults.removeItems([conversation]);
		}
	};

	public markAsSpam = async (id: string) => {
		if (!this.busy) {
			this.busy = true;
			const result = await this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
				this.composeApiUrl({ urlPath: `conversation/${id}/spam` }),
				'PUT',
				{}
			);

			if (result.success) {
				const archived = this.mConversationsPageCollectionController?.fetchResults.getById(id);
				this.mConversationsPageCollectionController?.fetchResults.removeItems([archived]);
				this.busy = false;
			} else {
				this.busy = false;
				throw Api.asApiError(result);
			}
		}
	};

	// Re-insert a conversation into the local inbox list after it is unmarked as spam, so the
	// sidebar reflects the restored state without waiting for a refetch. Dedupes against any
	// stale local copy or realtime-event-added entry. Called by the unmark-as-spam mutation
	// from `useUnmarkConversationAsSpamMutation` after a successful API call.
	public onUnmarkConversationAsSpam = (conversation: ConversationViewModel) => {
		const list = this.mConversationsPageCollectionController?.fetchResults;
		if (list && !list.find(x => x.id === conversation.id)) {
			list.unshift(conversation);
		}
	};

	toggleUnsubscribeConversation = async (conversation: ConversationViewModel) => {
		if (!this.busy) {
			this.busy = true;
			const result = await this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
				this.composeApiUrl({ urlPath: `conversation/${conversation.id}/automatedSMSOptOut` }),
				conversation.automatedSMSOptOutDate ? 'DELETE' : 'PUT',
				null
			);

			if (result.success) {
				conversation.setConversation(result.value);
				this.busy = false;
			} else {
				this.busy = false;
				throw Api.asApiError(result);
			}
			return result.value;
		}
	};

	public updateTypingIndicator = (userId = this.mUserSession.user.id) => {
		return this.userSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `conversation/${userId}/typing` }),
			'PUT',
			{}
		);
	};

	public createConversation = async (phoneNumbers: string[]) => {
		this.busy = true;
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `Conversation` }),
			'POST',
			{
				phoneNumberId: this.id,
				toPhoneNumbers: phoneNumbers,
			}
		);

		if (result.success) {
			let conversation = this.mConversationsPageCollectionController?.fetchResults.find(c => c.id === result.value.id);
			const index = this.mConversationsPageCollectionController?.fetchResults.indexOf(conversation);

			if (index > -1) {
				this.mConversationsPageCollectionController?.fetchResults.setItemAtIndex(conversation, index);
			} else {
				conversation = new ConversationViewModel(this.userSession, result.value, this.conversationCallbacks);
				this.mConversationsPageCollectionController?.fetchResults.unshift(conversation);
			}

			this.busy = false;
			return conversation;
		} else {
			this.busy = false;
			throw Api.asApiError(result);
		}
	};

	/**
	 * Checks the current conversations list for conversations match. Does not check server-side.
	 *
	 * @param id Conversation id
	 * @returns Boolean
	 */
	public hasConversation = (id: string) => {
		return !!this.mConversationsPageCollectionController?.fetchResults.find(x => x.id === id);
	};

	/**
	 * If the conversation does not exist in the vm's current list of conversations, it will attempt to resolve it from
	 * the server and add the result to it's conversations list before returning.
	 *
	 * @param id Conversation id
	 * @returns ConversationViewModel
	 */
	public getConversation = async (id: string) => {
		const existingConversation = this.mConversationsPageCollectionController?.fetchResults.find(x => x.id === id);
		if (existingConversation) {
			return existingConversation;
		}
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<IConversation>(
			this.composeApiUrl({ urlPath: `conversation/${id}` }),
			'GET'
		);

		if (result.success) {
			const conversation = new ConversationViewModel(this.userSession, result.value, this.conversationCallbacks);
			// Spam-marked conversations are filtered out of the standard list endpoints, so we
			// must not inject them back into the local list — they would render in the inbox
			// despite being spam. Deep-link consumers receive the VM directly.
			if (!result.value.isSpam) {
				this.mConversationsPageCollectionController?.fetchResults.unshift(conversation);
			}
			return conversation;
		} else {
			throw Api.asApiError(result);
		}
	};

	/**
	 * Will only add those conversations that are not already part of the collection
	 */
	addConversations = (conversations: IConversation[]) => {
		if (conversations?.length) {
			const toAdd = conversations.filter(x => !this.hasConversation(x.id));
			const vms = toAdd.map(x => new ConversationViewModel(this.userSession, x, this.conversationCallbacks));
			if (vms?.length) {
				this.mConversationsPageCollectionController?.fetchResults.addAll(vms, 'unshift');
			}
		}
	};

	public getConversationByPhoneNumbers = async (phoneNumbers: Api.IPhoneNumber[]) => {
		const contactPhoneNumbers = phoneNumbers.map(n => n?.metadata?.standard ?? n?.value ?? null);
		if (contactPhoneNumbers?.length > 0) {
			const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<IConversation>(
				this.composeApiUrl({ urlPath: `conversation/phoneNumber/${this.id}/conversation` }),
				'POST',
				{ contactPhoneNumbers }
			);
			if (result.success) {
				const convo = this.mConversationsPageCollectionController?.fetchResults.getById(result.value.id);
				if (convo) {
					return convo;
				} else {
					const conversation = new ConversationViewModel(this.userSession, result.value, this.conversationCallbacks);
					this.mConversationsPageCollectionController?.fetchResults.unshift(conversation);
					return conversation;
				}
			} else {
				// intentionally swallowing error
			}
		}
	};

	public removeConversationById = (id: string) => {
		const conversation = this.mConversationsPageCollectionController?.fetchResults.find(x => x.id === id);
		this.removeConversation(conversation);
	};

	public removeConversation = (conversation: ConversationViewModel) => {
		if (!conversation) {
			return null;
		}
		return this.mConversationsPageCollectionController?.fetchResults.removeItems([conversation]);
	};

	public getConversations = async ({ unreadOnly = false }: { unreadOnly?: boolean } = {}) => {
		this.mLoadingConversations = true;
		await this.mConversationsPageCollectionController?.getNext({
			unreadOnly,
		});
		this.mLoadingConversations = false;
		this.mConversationsLoaded = true;
	};

	public fullReset = () => {
		this.mEventsViewModel?.permananentlyDisconnect();
		this.mPhoneNumberOrders = [];
		this.reset();
	};

	public getAllUnreadMessages = async () => {
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<number>(
			this.composeApiUrl({ urlPath: `phoneNumber/${this.id}/unread` }),
			'GET'
		);

		if (result.success) {
			this.mTotalUnreadMessages = result.value;
		} else {
			throw Api.asApiError(result);
		}
	};

	public getUserPhone = async () => {
		if (VmUtils.canViewTexting(this.userSession)) {
			this.loading = true;
			const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ITelephonyConfiguration[]>(
				this.composeApiUrl({ urlPath: 'PhoneNumber/user' }),
				'GET'
			);

			if (result.success) {
				runInAction(() => {
					this.mPhoneNumberOrders = result.value;
					if (this.id) {
						this.getAllUnreadMessages();
					}
					this.loading = false;
					this.loaded = true;
				});
			} else {
				this.loading = false;
				throw Api.asApiError(result);
			}
		}
	};

	protected onTextMessageCreated = (events: Api.IRemoteEvent<ITextMessageCreatedRemoteResourceEvent>[]) => {
		for (const event of events) {
			const convo = this.mConversationsPageCollectionController?.fetchResults.find(
				c => c.id === event.value.conversationId
			);
			if (convo) {
				convo.getMessage(event.value.id);
			} else {
				this.getConversation(event.value.conversationId);
			}
		}
		this.sortConversations();
		this.getAllUnreadMessages();
	};

	protected onTextMessageUpdated = (events: Api.IRemoteEvent<ITextMessageUpdatedRemoteResourceEvent>[]) => {
		for (const event of events) {
			if (event.value.status === TextStatus.Delivered) {
				const convo = this.mConversationsPageCollectionController?.fetchResults.find(
					c => c.id === event.value.conversationId
				);
				if (convo) {
					convo.getMessage(event.value.id);
				} else {
					this.getConversation(event.value.conversationId);
				}
			}
		}
		this.sortConversations();
		this.getAllUnreadMessages();
	};

	protected connect = (eventLogger: Api.IEventLoggingService, stompConfig?: StompJs.StompConfig) => {
		this.mEventsViewModel = new TextMessagingEventsViewModel(this.mUserSession, {}, eventLogger);
		this.mEventsViewModel.onTextMessageCreated = this.onTextMessageCreated;
		this.mEventsViewModel.onTextMessageUpdated = this.onTextMessageUpdated;
		this.mEventsViewModel.connect(stompConfig);
	};

	public init = (eventLogger: Api.IEventLoggingService, stompConfig?: StompJs.StompConfig) => {
		if (VmUtils.canViewTexting(this.userSession)) {
			this.initConversations();
			this.getUserPhone();
			this.connect(eventLogger, stompConfig);
		}
	};

	public reset = () => {
		this.loaded = undefined;
		this.loading = undefined;
		this.busy = undefined;
		this.mLoadingConversations = false;
		this.mConversationsPageCollectionController?.reset();
		this.mConversationsPageCollectionController = null;
	};

	protected sortConversations = () => {
		this.mConversationsPageCollectionController?.fetchResults.sort((a, b) => {
			const dateA = new Date(a?.lastMessage?.creationDate);
			const dateB = new Date(b?.lastMessage?.creationDate);
			if (dateA < dateB) {
				return 1;
			} else if (dateB < dateA) {
				return -1;
			} else {
				return 0;
			}
		});
	};

	public normalizePhoneNumber = async (phoneNumber: string) => {
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IPhoneNumberMetadata>(
			this.composeApiUrl({ urlPath: `phoneNumber/normalize/${phoneNumber}` }),
			'GET'
		);

		if (result.success) {
			return result.value;
		} else {
			throw Api.asApiError(result);
		}
	};

	protected initConversations = () => {
		this.mConversationsPageCollectionController = new ObservablePageCollectionController<
			IConversation,
			ConversationViewModel
		>({
			apiPath: () => this.conversationApiUrl,
			client: this.userSession.webServiceHelper,
			httpMethod: 'GET',
			transformer: this.mTransformConversation,
		});
	};
}

export class TextRecipientViewModel extends ViewModels.ContactViewModel {
	protected mSelectedPhoneNumber: Api.IPhoneNumber;

	metadata?: Api.IPhoneNumberMetadata;
	value?: string;

	constructor(userSession: UserSessionContext, contact: Api.IContact) {
		super(userSession, contact);

		makeObservable<TextRecipientViewModel, 'mSelectedPhoneNumber'>(this, {
			mSelectedPhoneNumber: observable,
			selectedPhoneNumber: computed,
			needsResolving: computed,
			textingCapablePhoneNumbers: computed,
			setSelectedPhoneNumber: action,
		});

		const textingCapablePhoneNumbers = this.textingCapablePhoneNumbers;
		if (textingCapablePhoneNumbers?.length === 1) {
			this.mSelectedPhoneNumber = textingCapablePhoneNumbers[0];
		}
	}

	get selectedPhoneNumber() {
		return this.mSelectedPhoneNumber && this.entity.phoneNumbers.find(x => x.value === this.mSelectedPhoneNumber.value)
			? this.mSelectedPhoneNumber
			: null;
	}

	get needsResolving() {
		const numbers = this.textingCapablePhoneNumbers;
		return (
			!numbers.length ||
			(numbers.length > 1 && !this.selectedPhoneNumber) ||
			(numbers.length === 1 && !numbers[0].metadata)
		);
	}

	get textingCapablePhoneNumbers() {
		return VmUtils.getTextMessageCapablePhoneNumbers(this.entity?.phoneNumbers);
	}

	public setSelectedPhoneNumber = (num: Api.IPhoneNumber) => {
		this.mSelectedPhoneNumber = num;
	};

	public toJs = () => {
		return {
			...this.entity,
			selectedPhoneNumber: this.selectedPhoneNumber,
		};
	};
}
